import React, { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';
import { useCityStore } from '../city/cityStore';
import { ROOMS } from '../rooms';
import { makeVenueScreen, SCREEN_ASPECT } from '../city/venueScreen';

// VenueScreens — a venue type's wall screens, playing whoever is hosting the room
// (city/venueScreen draws it). Mounted by MeshInterior inside the room's scale
// group, so it only exists while you are standing in that room and is gone —
// canvas, texture, timers — the moment you leave.
//
// ONE canvas for every screen in the room: they all show the same thing, so one
// redraw and one texture upload serve them all. A screen of another shape shows
// the middle of the picture (its UVs are cropped once, at build), which is why
// the canvas keeps everything that must be read inside a centre band.
//
// Unlit (MeshBasicMaterial) and a touch under white: in a room running at
// `light` 0.3 the screens ARE the light, and at full white the bloom pass turns
// a cover into a blown-out rectangle.

const FRAME = 0.035;   // the bezel, world units each side

function croppedPlane(w, h) {
  const geo = new THREE.PlaneGeometry(w, h);
  const a = w / h;
  let u0 = 0; let u1 = 1; let v0 = 0; let v1 = 1;
  if (a < SCREEN_ASPECT) { const k = a / SCREEN_ASPECT; u0 = 0.5 - k / 2; u1 = 0.5 + k / 2; }
  else if (a > SCREEN_ASPECT) { const k = SCREEN_ASPECT / a; v0 = 0.5 - k / 2; v1 = 0.5 + k / 2; }
  const uv = geo.attributes.uv;
  for (let i = 0; i < uv.count; i++) uv.setXY(i, u0 + uv.getX(i) * (u1 - u0), v0 + uv.getY(i) * (v1 - v0));
  uv.needsUpdate = true;
  return geo;
}

export default function VenueScreens({ roomId, screens, ratio = 1 }) {
  const venue = useWorldStore((s) => s.venueRegistry?.[roomId]);
  // A SET IN THIS ROOM (components/VenueStage, 2026-10-30): the walls show who
  // is on and what is playing, instead of the host's slideshow.
  const set = useCityStore((s) => (s.venueNow?.room === roomId ? s.venueNow : null));
  const screen = useMemo(() => makeVenueScreen(), []);
  const mats = useMemo(() => ({
    face: new THREE.MeshBasicMaterial({ map: screen.texture, toneMapped: false, color: new THREE.Color(0.88, 0.88, 0.88) }),
    bezel: new THREE.MeshBasicMaterial({ color: '#050405' }),
  }), [screen]);
  const geos = useMemo(() => screens.map((sc) => ({
    face: croppedPlane(sc.size[0] * ratio, sc.size[1] * ratio),
    bezel: new THREE.PlaneGeometry(sc.size[0] * ratio + FRAME * 2, sc.size[1] * ratio + FRAME * 2),
  })), [screens, ratio]);
  useEffect(() => () => { geos.forEach((g) => { g.face.dispose(); g.bezel.dispose(); }); }, [geos]);
  useEffect(() => () => { mats.face.dispose(); mats.bezel.dispose(); screen.dispose(); }, [mats, screen]);

  // What to draw, built when the venue or its host changes — never per frame.
  // The host comes from the store rather than ROOMS, because hosts change all
  // day and a ROOMS write renders nothing.
  const st = useMemo(() => {
    const room = ROOMS[roomId] || {};
    const name = venue?.name || room.name || '';
    if (set) {
      // Live: the song's own cover (their words: every cover in the set);
      // about to start: "up next" over the set's covers.
      const label = !set.live ? `UP NEXT AT ${name.toUpperCase()}`
        : set.words || !set.title ? `LIVE AT ${name.toUpperCase()}`
          : `NOW PLAYING · ${String(set.title).toUpperCase()}`;
      return {
        title: name.toUpperCase(),
        label: label.length > 60 ? `${label.slice(0, 59)}…` : label,
        hostName: String(set.name || '').toUpperCase(),
        media: set.live && set.art && !set.words ? [set.art] : (set.arts || []),
        accent: venue?.accent || room.accent,
      };
    }
    const host = venue ? venue.host : (room.hostMedia?.length ? { name: '', media: room.hostMedia } : null);
    return {
      title: name.toUpperCase(),
      label: `TONIGHT AT ${name.toUpperCase()}`,
      hostName: host?.name ? host.name.toUpperCase() : '',
      media: host?.media || [],
      accent: venue?.accent || room.accent,
    };
  }, [venue, roomId, set]);
  const stRef = useRef(st);
  stRef.current = st;

  useFrame(() => { screen.draw(stRef.current); });

  return (
    <group>
      {screens.map((sc, i) => (
        <group key={i} position={[sc.pos[0] * ratio, sc.pos[1] * ratio, sc.pos[2] * ratio]} rotation={[0, sc.rotY || 0, 0]}>
          <mesh geometry={geos[i].bezel} material={mats.bezel} position={[0, 0, -0.004]} />
          <mesh geometry={geos[i].face} material={mats.face} />
        </group>
      ))}
    </group>
  );
}
